import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Tabs } from "@shared/ui/tabs";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
import {
  useMeterReadingFormatDetail,
  usePatchMeterReadingFormat,
  usePostMeterReadingFormat,
  useUserUtilityServices,
} from "../../hooks";
import { logEvent } from "@shared/analytics/analytics";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { useFormat } from "../../utils";
import FieldConfiguration from "../FieldConfiguration";
import FieldSelector from "./FieldSelector";
import MobilePreview from "./MobilePreview";

const AddEditFormats = () => {
  const { id } = useParams();
  const { state, dispatch } = useFormat();
  const [formatName, setFormatName] = useState(state.currentFormat?.name || "");
  const [readType, setReadType] = useState<any>(state.currentFormat?.readType);
  const [utilityService, setUtilityService] = useState<any>(
    state.currentFormat?.utilityService
  );
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: utilityServices } = useUserUtilityServices(remoteUtilityId);
  const { data: readTypes } = useChoices({ choice: "meter_type" });

  const activeUtilityServices = utilityServices?.filter(
    (service) => service.isActive
  );
  const utilityServicesList = mapToLabelValue(
    activeUtilityServices,
    "name",
    "name"
  );
  const readTypeList = mapToLabelValue(readTypes, "value", "key");
  const { mutate: createMeterFormat } = usePostMeterReadingFormat();
  const { data: formatDetail } = useMeterReadingFormatDetail(
    id,
    remoteUtilityId
  );
  const { mutate: updateFormat } = usePatchMeterReadingFormat();
  const {startTracking, stopTracking } = useFormCompletionTracker('MX Meter Read Format');
  const handleBackToDashboard = () => {
    dispatch({ type: "SET_STEP", payload: { step: 0 } });
    navigate(-1);
  };

  const handleDeploy = (isActive: boolean) => {
    if (!formatName || !utilityService || !readType) {
     toast({
  variant: "default",
  title: "Info",
  description: "Missing required fields: formatName, utilityService, or readType",
});

      return;
    }

    const configurationJson = state.selectedFields.reduce(
      (acc, field, index) => {
        acc[field.fieldName] = {
          field_name: field.fieldName,
          sequence_id: index + 1,
          input_type: field.inputMethod,
          field_type: field.fieldType,
          display_type: field.displayType,
          values: field.values,
          required: field.required ?? false,
          is_active: true,
        };
        return acc;
      },
      {} as Record<string, any>
    );

    const payload = {
      remote_utility_id: remoteUtilityId,
      name: formatName,
      utility_service: utilityService,
      meter_type: Number(readType),
      configuration_json: configurationJson,
      is_active: isActive,
    };

    if (id) {
      updateFormat({ id: Number(id), payload });
    } else {
      const requiredKeys = ["fieldName", "inputMethod", "displayType"];

      // Validate each selected field
      for (const field of state.selectedFields) {
        const missing = [...requiredKeys];

        // Conditionally require `fieldType` only for AUTOMATED fields
        if (field.inputMethod === "MANUAL" || "Manual") {
          missing.push("fieldType");
        }
        if (field.inputMethod === "AUTOMATED" || "Automated") {
          missing.pop();
        }

        const missingFields = missing.filter((key) => !field[key]);

        if (missingFields.length > 0) {
toast({
  title: "Warning",
  description: "Please apply the changes before deploying.",
  variant: "destructive", // You can keep "destructive" for warning/error style
});
          return;
        }
      }
      createMeterFormat(payload, {
        onSuccess: () => {
          stopTracking();
        }
      });
    }
    navigate("/mx/settings/meterread-format");

    dispatch({ type: "SET_STEP", payload: { step: 2 } });
  };

  const hasHydrated = useRef(false);

  useEffect(() => {
    startTracking();
    if (!id || !formatDetail || hasHydrated.current) return;
    hasHydrated.current = true;

    setFormatName(formatDetail.name);
    setReadType(String(formatDetail.meterType));
    setUtilityService(formatDetail.utilityService);

    const fieldsFromConfig = Object.entries(
      formatDetail.configurationJson || {}
    ).map(([fieldKey, config]: [string, any]) => ({
      id: fieldKey,
      fieldName: config.fieldName,
      inputMethod: config.inputType,
      fieldType: config.fieldType,
      values: config.values,
      displayType: config.displayType,
      required: config.required ?? false,
      isSelected: true,
      sequence_id: config.sequenceId,
    }));

    dispatch({ type: "SET_ALL_FIELDS", payload: { fields: fieldsFromConfig } });

    dispatch({
      type: "CREATE_FORMAT",
      payload: {
        name: formatDetail.name,
        utilityService: formatDetail.utilityService,
        readType: formatDetail.meterType,
      },
    });
  }, [id, formatDetail]);
  const tabComponents = {
    fields: {
      label: "Field Configuration",
      shortLabel: "Fields",
      // component: (
      //   <Card>
      //     <CardContent className="p-4">
      //       {state.selectedField ? (
      //         <FieldConfiguration field={state.selectedField} />
      //       ) : (
      //         <div className="text-center p-6 text-gray-500">
      //           <p>Select a field from the list to configure it</p>
      //           <p className="text-sm mt-2">
      //             Choose any field to customize its settings and behavior
      //           </p>
      //         </div>
      //       )}
      //     </CardContent>
      //   </Card>
      // ),
      component: <FieldConfiguration field={state.selectedField} />,
    },
    preview: {
      label: "Mobile Preview",
      shortLabel: "Preview",
      component: (
        <Card>
          <CardContent className="p-4">
            <MobilePreview />
          </CardContent>
        </Card>
      ),
      // component: <></>
    },
  };

  return (
    <div className="container-fluid p-4 max-w-full space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Format Configuration</CardTitle>
          <p className="text-sm text-gray-600">
            Set up your meter reading format with basic information and field
            selection
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="format-name">Format Name *</Label>
              <Input
                id="format-name"
                value={formatName}
                onChange={(e) => setFormatName(e.target.value)}
                placeholder="Enter format name"
              />
            </div>
            <div>
              <Label htmlFor="read-type">Read Type *</Label>
              <Select value={readType} onValueChange={setReadType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select read type" />
                </SelectTrigger>
                <SelectContent>
                  {readTypeList.map((type) => (
                    <SelectItem key={type.value} value={String(type.value)}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="utility-service">Utility Service *</Label>
              <Select value={utilityService} onValueChange={setUtilityService}>
                <SelectTrigger>
                  <SelectValue placeholder="Select utility service" />
                </SelectTrigger>
                <SelectContent>
                  {utilityServicesList.map((utilityService) => (
                    <SelectItem
                      key={utilityService.value}
                      value={String(utilityService.value)}
                    >
                      {utilityService.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3">
          <Card className="h-full">
            <CardContent className="p-4">
              <FieldSelector />
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-6">
          <Tabs
            defaultValue="fields"
            tabComponents={tabComponents}
            tabsListClassName="grid grid-cols-2 mb-4"
            className="w-full"
            urlMapping={{
              fields: "fields",
              preview: "preview",
            }}
          />
        </div>

        <div className="lg:col-span-3">
          <Card className="h-full">
            <CardContent className="p-4">
              <h2 className="text-lg font-semibold mb-3">Selected Fields</h2>
              <p className="text-sm text-gray-600 mb-4">
                Review and manage your chosen fields
              </p>
              <SelectedFieldsSummary />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={handleBackToDashboard}>
          ← Back to Dashboard
        </Button>
        <div className="space-x-4">
          {!id && (
            <Button onClick={() => handleDeploy(false)}>
              Save as Template
            </Button>
          )}
          <Button onClick={() => handleDeploy(true)}>
            {id ? "Edit" : "Deploy"} Format →
          </Button>
        </div>
      </div>
    </div>
  );
};

const SelectedFieldsSummary = () => {
  const { state, dispatch } = useFormat();
  const selectedFields = state.selectedFields;

  if (selectedFields.length === 0) {
    return (
      <div className="text-center text-gray-500 py-6">
        <p>No fields selected</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
      {selectedFields.map((field) => (
        <div
          key={field.id}
          className="p-2 border border-gray-100 rounded-md bg-white flex items-center justify-between"
        >
          <div className="flex items-center">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm">{field.fieldName}</span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 p-0 px-2"
            onClick={() =>
              dispatch({ type: "SELECT_FIELD", payload: { field } })
            }
          >
            Edit
          </Button>
        </div>
      ))}
    </div>
  );
};

export default AddEditFormats;
